import React, { HTMLProps } from 'react';
import { styled } from '@mui/material';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers';
import moment from 'moment/moment';

interface DayProps {
  dateJs: moment.Moment;
  selectedDates: Array<any | null>;
  pickersDayProps: PickersDayProps<any>;
}

interface CustomPickerDayProps {
  dayIsBetween: boolean;
  isFirstDay: boolean;
  isLastDay: boolean;
  isSaturday?: boolean;
  isToday?: boolean;
  isSunday?: boolean;
  isStartDayOfMonth?: boolean;
  isLastDayOfMonth?: boolean;
}

const PickerDayWrapper = styled('div')<CustomPickerDayProps & HTMLProps<HTMLDivElement>>(
  ({
    theme,
    dayIsBetween,
    isFirstDay,
    isLastDay,
    isSaturday,
    isSunday,
    isLastDayOfMonth,
    isStartDayOfMonth,
  }) => ({
    padding: '0 2px',
    ...(dayIsBetween && {
      borderRadius: 0,
      backgroundColor: theme.palette.primary.light,
    }),
    ...(isFirstDay && {
      paddingLeft: '0',
      marginLeft: '2px',
      paddingRight: '2px',
      marginRight: '0',
      backgroundColor: theme.palette.primary.light,
      borderTopLeftRadius: '50%',
      borderBottomLeftRadius: '50%',
      borderTopRightRadius: '0px',
      borderBottomRightRadius: '0px',
    }),
    ...(isLastDay && {
      paddingLeft: '2px',
      marginLeft: '0',
      paddingRight: '0',
      marginRight: '2px',
      backgroundColor: theme.palette.primary.light,
      borderTopRightRadius: '50%',
      borderBottomRightRadius: '50%',
      borderTopLeftRadius: '0px',
      borderBottomLeftRadius: '0px',
    }),
    ...(dayIsBetween &&
      isSunday && {
        borderTopLeftRadius: '50%',
        borderBottomLeftRadius: '50%',
      }),
    ...(dayIsBetween &&
      isSaturday && {
        borderTopRightRadius: '50%',
        borderBottomRightRadius: '50%',
      }),
    ...(isLastDay &&
      isSunday && {
        paddingLeft: 0,
        padding: '0',
        margin: '0 2px',
        borderRadius: '50%',
      }),
    ...(isFirstDay &&
      isSaturday && {
        paddingLeft: 0,
        padding: '0',
        margin: '0 2px',
        borderRadius: '50%',
      }),
    ...(isLastDayOfMonth &&
      dayIsBetween && {
        borderTopRightRadius: '50%',
        borderBottomRightRadius: '50%',
      }),
    ...(isLastDayOfMonth &&
      !isLastDay && {
        borderTopRightRadius: '50%',
        borderBottomRightRadius: '50%',
      }),
    ...(isLastDayOfMonth &&
      isFirstDay && {
        padding: '0 !important',
        margin: '0 2px',
      }),
    ...(isStartDayOfMonth &&
      !isFirstDay && {
        borderTopLeftRadius: '50%',
        borderBottomLeftRadius: '50%',
      }),
    ...(isStartDayOfMonth &&
      isLastDay && {
        padding: '0 !important',
        margin: '0 2px',
      }),
    ...(isFirstDay &&
      isLastDay && {
        borderRadius: '50%',
        margin: '0 2px',
        padding: '0 !important',
      }),
  }),
);

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) =>
    prop !== 'dayIsBetween' && prop !== 'isFirstDay' && prop !== 'isLastDay',
})<CustomPickerDayProps & PickersDayProps<any>>(
  ({ theme, dayIsBetween, isFirstDay, isLastDay, isToday }) => ({
    '&:hover': {
      border: '1px solid ' + theme.palette.neutral.main,
    },
    ...(isToday && { border: '1px solid ' + theme.palette.neutral.main }),
    ...(dayIsBetween && {
      backgroundColor: `${theme.palette.primary.light} !important`,
      color: `${theme.palette.common.black} !important`,
      '&:hover, &:focus': {
        backgroundColor: theme.palette.primary.main,
      },
    }),
    ...(isFirstDay && {
      borderTopLeftRadius: '50%',
      borderBottomLeftRadius: '50%',
    }),
    ...(isLastDay && {
      borderTopRightRadius: '50%',
      borderBottomRightRadius: '50%',
    }),
  }),
);

export const Day = (props: DayProps) => {
  const { selectedDates, pickersDayProps, dateJs } = props;
  if (!selectedDates || !selectedDates.length || pickersDayProps.outsideCurrentMonth) {
    return <PickersDay {...pickersDayProps} />;
  }

  const date = moment(dateJs);

  const start = selectedDates[0] ? moment(selectedDates[0]) : null;
  const end = selectedDates[selectedDates.length - 1]
    ? moment(selectedDates[selectedDates.length - 1])
    : null;

  const dayIsBetween = date.isBetween(start, end, null, '()');
  const isFirstDay = date.isSame(start, 'day');
  const isLastDay = date.isSame(end, 'day');
  const endOfMonth = moment(date).endOf('month');
  const startOfMonth = moment(date).startOf('month');
  const isLastDayOfMonth = date.isSame(endOfMonth, 'day');
  const isStartDayOfMonth = date.isSame(startOfMonth, 'day');
  const dayOfWeek = date.day();
  const isToday = date.isSame(moment(), 'day');
  const isSaturday = dayOfWeek === 6;
  const isSunday = dayOfWeek === 0;

  return (
    <PickerDayWrapper
      dayIsBetween={dayIsBetween}
      isFirstDay={isFirstDay}
      isSaturday={isSaturday}
      isLastDayOfMonth={isLastDayOfMonth}
      isStartDayOfMonth={isStartDayOfMonth}
      isSunday={isSunday}
      isLastDay={isLastDay}
    >
      <CustomPickersDay
        {...pickersDayProps}
        disableMargin
        isToday={isToday}
        dayIsBetween={dayIsBetween}
        isFirstDay={isFirstDay}
        isLastDay={isLastDay}
      />
    </PickerDayWrapper>
  );
};
